import { fmt, fmtDate } from '@dcc-erp/shared';
import type { BankStatementWithChildren } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { lineMovement, num } from './recon-helpers';

interface Props {
  statement: BankStatementWithChildren | undefined;
  isPending: boolean;
  error?: string | null;
  onFinalize: () => void;
  onClose: () => void;
}

function CheckRow({ ok, text }: { ok: boolean; text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '10px 12px',
        background: ok ? '#e7f4ec' : '#fef3e8',
        border: `1px solid ${ok ? '#bbdebf' : '#f0dab0'}`,
        borderRadius: 6,
      }}
    >
      <span style={{ color: ok ? 'var(--positive)' : 'var(--warning)', fontWeight: 700 }}>
        {ok ? '✓' : '!'}
      </span>
      <span style={{ fontSize: 14 }}>{text}</span>
    </div>
  );
}

export function FinalizeModal({ statement, isPending, error, onFinalize, onClose }: Props) {
  const openCount = statement ? statement.lines.filter((l) => l.status === 'OPEN').length : 0;
  const opening = statement ? num(statement.openingBalance) : 0;
  const closing = statement ? num(statement.closingBalance) : 0;
  const movement = statement ? lineMovement(statement.lines) : 0;
  const balanceOk = Math.round((opening + movement) * 100) === Math.round(closing * 100);
  const isDraft = statement?.status === 'DRAFT';
  const ready = !!statement && isDraft && openCount === 0 && balanceOk;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 640,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>ปิดกระทบยอด statement</h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ปิดทีละ statement — ตรวจความพร้อมก่อนล็อก
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!statement ? (
            <div style={{ padding: 14, background: '#fef3e8', borderRadius: 6 }}>
              ยังไม่ได้เลือก statement — เลือกบัญชีจากแท็บ "ภาพรวมทุกบัญชี" ก่อน
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#fafafa',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                <b>
                  {statement.bankCode} {statement.bankAccNo}
                </b>{' '}
                · งวด {fmtDate(statement.statementDate)} · ยอดปิด {fmt(closing)}
              </div>
              <CheckRow
                ok={openCount === 0}
                text={
                  openCount === 0
                    ? 'ทุกรายการเคลียร์แล้ว (จับคู่/ปรับปรุง)'
                    : `ยังมี ${openCount} รายการค้าง (OPEN) — จับคู่หรือปรับปรุงก่อน`
                }
              />
              <CheckRow
                ok={balanceOk}
                text={
                  balanceOk
                    ? 'ยอดยกมา + เคลื่อนไหว = ยอดปิด (สมการตรง)'
                    : 'สมการไม่ตรง — ยอดยกมา + เคลื่อนไหว ≠ ยอดปิด'
                }
              />
              <CheckRow
                ok={!!isDraft}
                text={isDraft ? 'สถานะ DRAFT พร้อมปิด' : 'statement นี้ปิด (RECONCILED) ไปแล้ว'}
              />
              {error && <div style={{ color: 'var(--negative)', fontSize: 14 }}>{error}</div>}
              <div
                style={{
                  padding: '10px 12px',
                  background: '#fef3e8',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                หลังปิด statement จะถูกล็อกเป็น RECONCILED · รายการปรับปรุง (JV) ถูกลงตอนเพิ่มไปแล้ว
                — การปิดไม่สร้าง JV เพิ่ม
              </div>
            </>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <button className="btn" disabled title="เฟสถัดไป">
            <Icon name="download" size={14} /> Preview รายงาน
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={onClose}>
              ยกเลิก
            </button>
            <button className="btn primary" disabled={!ready || isPending} onClick={onFinalize}>
              <Icon name="check" size={14} /> ปิดกระทบยอด statement นี้
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
